import { z } from "zod";

/**
 * Regole sull'identità pubblica di una persona.
 *
 * Il nickname serve a farsi trovare dagli amici **senza dare il numero di
 * telefono**: è quindi un identificatore pubblico, e come tale ha bisogno di
 * regole rigide su formato e collisioni.
 */

export const NICKNAME_MIN_LENGTH = 3;
export const NICKNAME_MAX_LENGTH = 20;

/** Solo minuscole, cifre e underscore: niente maiuscole, niente omografi. */
export const NICKNAME_PATTERN = /^[a-z0-9_]+$/;

/**
 * Nickname che non possono essere rivendicati da un utente.
 * Servono a impedire che qualcuno si spacci per il servizio, e a tenere libere
 * le parole che potrebbero diventare rotte pubbliche (`qota.app/support`).
 */
export const RESERVED_NICKNAMES = new Set([
  "admin",
  "administrator",
  "qota",
  "support",
  "help",
  "info",
  "root",
  "system",
  "staff",
  "team",
  "moderator",
  "security",
  "billing",
  "settings",
  "account",
  "login",
  "signup",
  "invite",
  "api",
  "app",
  "www",
  "me",
  "null",
  "undefined",
]);

export const nicknameSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(NICKNAME_MIN_LENGTH, `Almeno ${NICKNAME_MIN_LENGTH} caratteri`)
  .max(NICKNAME_MAX_LENGTH, `Al massimo ${NICKNAME_MAX_LENGTH} caratteri`)
  .regex(NICKNAME_PATTERN, "Solo lettere minuscole, numeri e underscore")
  .refine((value) => !RESERVED_NICKNAMES.has(value), "Questo nickname è riservato")
  // Un nickname tutto numerico si confonderebbe con un id.
  .refine((value) => !/^\d+$/.test(value), "Il nickname non può essere solo numeri");

export const displayNameSchema = z
  .string()
  .trim()
  .min(1, "Il nome non può essere vuoto")
  .max(60, "Al massimo 60 caratteri");

/** Tinte disponibili per l'avatar, dai token del design system. */
export const AVATAR_TINTS = ["lavender", "grey", "sky", "sand", "blush", "mint"] as const;
export type AvatarTint = (typeof AVATAR_TINTS)[number];
export const avatarTintSchema = z.enum(AVATAR_TINTS);

// --- Contratti HTTP ---------------------------------------------------------

export const profileResponseSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string(),
  nickname: z.string().nullable(),
  avatarTint: avatarTintSchema,
  createdAt: z.string(),
});
export type ProfileResponse = z.infer<typeof profileResponseSchema>;

export const updateProfileRequestSchema = z
  .object({
    name: displayNameSchema.optional(),
    nickname: nicknameSchema.optional(),
    avatarTint: avatarTintSchema.optional(),
  })
  .refine(
    (value) => Object.values(value).some((field) => field !== undefined),
    "Serve almeno un campo da aggiornare",
  );
export type UpdateProfileRequest = z.infer<typeof updateProfileRequestSchema>;

export const nicknameAvailabilityResponseSchema = z.object({
  nickname: z.string(),
  available: z.boolean(),
  /** Perché non è disponibile: `taken`, `reserved`, `invalid`. */
  reason: z.enum(["taken", "reserved", "invalid"]).nullable(),
});
export type NicknameAvailabilityResponse = z.infer<typeof nicknameAvailabilityResponseSchema>;
